// Markdown-It plugin: Obsidian-style image embeds ![[target|modifier]]
// - target: URL (http/https/data) or plain string (resolver hook can map to URL)
// - modifier: size (e.g., 300 or 300x200) or alt text

const SIZE_RE = /^(\d+)(x(\d+))?$/
const WIKI_PIPE_TOKEN = '__KB_PIPE__'

export function markdownItWikiEmbeds(md, options = {}) {
  const resolveUrl = typeof options.resolveUrl === 'function' ? options.resolveUrl : (t) => t

  function tokenize(state, silent) {
    const max = state.posMax
    const start = state.pos
    const src = state.src
    if (src.charCodeAt(start) !== 0x21 /* ! */) return false
    if (start + 4 >= max) return false
    // Allow optional whitespace between '!' and '[['
    let look = start + 1
    while (look < max && /\s/.test(src.charAt(look))) look++
    if (look + 1 >= max) return false
    if (src.charCodeAt(look) !== 0x5b /* [ */ || src.charCodeAt(look + 1) !== 0x5b /* [ */)
      return false

    let pos = look + 2
    // Find the closing ']]' without crossing paragraph boundaries
    while (pos < max) {
      if (src.charCodeAt(pos) === 0x5d /* ] */ && src.charCodeAt(pos + 1) === 0x5d /* ] */) break
      pos++
    }
    if (pos >= max) return false

    // Extract inner content between the opening '![[' (accounting for optional whitespace)
    const inner = src
      .slice(look + 2, pos)
      .replace(/\r?\n/g, ' ')
      .trim()
    if (!inner) return false
    const normalizedInner = inner.split(WIKI_PIPE_TOKEN).join('|')

    if (!silent) {
      const pipe = normalizedInner.indexOf('|')
      let target = inner
      let modsRaw = ''
      if (pipe >= 0) {
        target = normalizedInner.slice(0, pipe).trim()
        modsRaw = normalizedInner.slice(pipe + 1).trim()
      }
      if (pipe < 0) {
        target = normalizedInner.trim()
      }

      let width = ''
      let height = ''
      let titleAttr = ''
      let altAttr = ''
      if (modsRaw) {
        const parts = modsRaw
          .split('|')
          .map((s) => s.trim())
          .filter(Boolean)
        for (const pRaw of parts) {
          const p = pRaw.trim()
          // size shorthand like 300 or 300x200
          const m = SIZE_RE.exec(p)
          if (m && !width) {
            width = m[1] || ''
            height = m[3] || ''
            continue
          }
          // key=value modifiers
          const kvIdx = p.indexOf('=')
          if (kvIdx > 0) {
            const k = p.slice(0, kvIdx).trim().toLowerCase()
            let v = p.slice(kvIdx + 1).trim()
            if ((v.startsWith('"') && v.endsWith('"')) || (v.startsWith("'") && v.endsWith("'"))) {
              v = v.slice(1, -1)
            }
            if (k === 'width' || k === 'w') {
              const mm = SIZE_RE.exec(v)
              if (mm) {
                width = mm[1] || width
                height = height || mm[3] || ''
              } else {
                width = (v.match(/\d+/) || [''])[0] || width
              }
              continue
            }
            if (k === 'height' || k === 'h') {
              const mm = /^(\d+)$/.exec(v)
              if (mm) height = mm[1]
              else height = (v.match(/\d+/) || [''])[0] || height
              continue
            }
            if (k === 'title') {
              titleAttr = v
              continue
            }
            if (k === 'alt') {
              altAttr = v
              continue
            }
            // ignore unknown keys for now
            continue
          }
          // No '=' and not a size: treat as title if none set yet
          if (!titleAttr) titleAttr = p
        }
      }

      // Support entity embeds like ![[character:ID|...]] by emitting a placeholder element
      const isUrlTarget = /^(https?:|data:)/i.test(target)
      const entityMatch = !isUrlTarget && /^([a-zA-Z0-9_.-]+):(.+)$/.exec(target)
      if (entityMatch) {
        const eType = entityMatch[1]
        const eId = entityMatch[2]
        // Support ![[type:id|Label|mode]] where second segment is display label, third is mode
        let displayLabel = ''
        let mode = 'inline' // default inline to keep text flow
        if (modsRaw) {
          const parts = modsRaw
            .split('|')
            .map((s) => (s || '').trim())
            .filter(Boolean)

          const isMode = (val) => {
            const m = String(val || '').toLowerCase()
            return m === 'inline' || m === 'avatar' || m === 'compact' || m === 'card'
          }

          if (parts.length >= 1) {
            if (isMode(parts[0])) {
              // First token is a mode, not a label
              const m = parts[0].toLowerCase()
              mode = m === 'avatar' ? 'inline' : m
            } else {
              // First token is a display label
              displayLabel = parts[0]
            }
          }

          if (parts.length >= 2) {
            const m = parts[1].toLowerCase()
            if (isMode(m)) mode = m === 'avatar' ? 'inline' : m
          } else if (!displayLabel) {
            // Single raw modifier that wasn't split or recognized as mode above
            const m = modsRaw.toLowerCase()
            if (isMode(m)) mode = m === 'avatar' ? 'inline' : m
          }
        }
        const escAttr = (s) =>
          String(s || '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;')
        const typeCls = `kb-et-${escAttr(eType)}`
        const idCls = `kb-eid-${escAttr(eId)}`
        const modeCls = mode ? ` kb-emode-${escAttr(mode)}` : ''
        const html = `<div class="kb-entity-embed ${typeCls} ${idCls}${modeCls}" data-type="${escAttr(
          eType
        )}" data-id="${escAttr(eId)}"${mode ? ` data-mode="${escAttr(mode)}"` : ''}${
          displayLabel ? ` data-display="${escAttr(displayLabel)}"` : ''
        }></div>`
        const t = state.push('html_inline', '', 0)
        t.content = html
        if (process.env && process.env.NODE_ENV !== 'production') {
          try {
            console.debug('[WikiEmbeds] entity placeholder', {
              type: eType,
              id: eId,
              mode,
              displayLabel
            })
          } catch {}
        }
        state.pos = pos + 2
        return true
      }

      const url = resolveUrl(target) || ''
      if (process.env && process.env.NODE_ENV !== 'production') {
        try {
          console.debug('[WikiEmbeds] match', {
            inner,
            target,
            url,
            width,
            height,
            titleAttr,
            altAttr
          })
        } catch {}
      }
      // If resolver returned any URL (absolute or app-relative), embed it
      if (url) {
        // Use raw HTML token to avoid renderer quirks and ensure robust output
        const esc = (s) =>
          String(s || '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/\"/g, '&quot;')
            .replace(/'/g, '&#39;')
        const imgHtml =
          `<img src="${url}"` +
          (altAttr ? ` alt="${esc(altAttr)}"` : titleAttr ? ` alt="${esc(titleAttr)}"` : '') +
          (titleAttr ? ` title="${esc(titleAttr)}"` : '') +
          ` loading="lazy" class="kb-embed"` +
          (width ? ` width="${width}"` : '') +
          (height ? ` height="${height}"` : '') +
          `>`
        const html = titleAttr
          ? `<figure class="kb-embed-figure">${imgHtml}<figcaption class="kb-embed-caption">${esc(
              titleAttr
            )}</figcaption></figure>`
          : imgHtml
        const t = state.push('html_inline', '', 0)
        t.content = html
      } else {
        // Fallback: render the literal text if we cannot resolve a URL
        const text = state.push('text', '', 0)
        text.content = `![[${normalizedInner}]]`
        if (process.env && process.env.NODE_ENV !== 'production') {
          try {
            console.warn('[WikiEmbeds] Unresolved embed target:', target)
          } catch {}
        }
      }
    }

    state.pos = pos + 2
    return true
  }

  // Ensure embed runs before wikilinks if present; otherwise before 'link'
  try {
    md.inline.ruler.before('wikilink', 'wiki_embed', tokenize)
  } catch {
    md.inline.ruler.before('link', 'wiki_embed', tokenize)
  }
}

export default markdownItWikiEmbeds
